import React from 'react';
import AnalyticsNavigationTabs from '../../../../../uiToolkit/Analytics/AnalyticsNavigationTabs';
import {courseLevelAnalyticsTabs} from '../../conf';
import AnalyticsEmptyState from '../../../../../uiToolkit/Analytics/AnalyticsEmptyState';
import {AnalyticsCommentsSkeleton} from '../../../components/AnalyticsSkeletons';
import {CourseComment} from '../../../../../data/models/Comments/CommentModel';
import {GradebookLesson} from '../../../../../data/models/Course/Gradebook';
import {analyticsText} from '../../../analyticsText';
import {getFirstCapitals} from '../../../../../utils/getFirstCapitals';
import {formatTimeAgo} from '../../../../../utils/notificationTimeTextConvertor';
import './styles.css';

interface Props {
  organizationId: string;
  courseId: string;
  comments: CourseComment[];
  microreels: GradebookLesson[];
  isLoading: boolean;
  search: string;
  setSearch: (search: string) => void;
  onExport: () => void;
}

interface CommentThread {
  root: CourseComment;
  replies: CourseComment[];
}

interface CommentsSection {
  key: string;
  title: string;
  isMicroreel: boolean;
  threads: CommentThread[];
}

// Two levels only: every reply hangs from its root comment
const buildThreads = (sectionComments: CourseComment[]): CommentThread[] => {
  const byId = new Map(sectionComments.map(x => [x.id, x]));
  const getRootId = (comment: CourseComment) => {
    let current = comment;
    while (current.parentCommentId && byId.has(current.parentCommentId)) {
      current = byId.get(current.parentCommentId)!;
    }
    return current.id;
  };
  const sorted = [...sectionComments].sort((x, y) =>
    x.dateCreated < y.dateCreated ? -1 : 1,
  );
  const threads = new Map<string, CommentThread>();
  sorted
    .filter(comment => getRootId(comment) === comment.id)
    .forEach(root => threads.set(root.id, {root, replies: []}));
  sorted
    .filter(comment => getRootId(comment) !== comment.id)
    .forEach(reply => threads.get(getRootId(reply))?.replies.push(reply));
  return Array.from(threads.values()).reverse();
};

const CourseAnalyticsCommentsUI = React.memo(
  ({
    organizationId,
    courseId,
    comments,
    microreels,
    isLoading,
    search,
    setSearch,
    onExport,
  }: Props) => {
    const matchesSearch = (comment: CourseComment) => {
      const term = search.toLowerCase();
      return (
        (comment.userName || '').toLowerCase().includes(term) ||
        comment.text.toLowerCase().includes(term)
      );
    };

    const getSections = (): CommentsSection[] => {
      const knownIds = new Set(microreels.map(reel => reel.lessonId));
      const sections: CommentsSection[] = microreels.map(reel => ({
        key: reel.lessonId,
        title: reel.title,
        isMicroreel: true,
        threads: buildThreads(
          comments.filter(x => x.microreelId === reel.lessonId),
        ),
      }));
      sections.push({
        key: 'other',
        title: analyticsText.otherComments,
        isMicroreel: false,
        threads: buildThreads(
          comments.filter(x => !x.microreelId || !knownIds.has(x.microreelId)),
        ),
      });
      return sections
        .map(section => ({
          ...section,
          threads: search
            ? section.threads.filter(
                thread =>
                  matchesSearch(thread.root) ||
                  thread.replies.some(matchesSearch),
              )
            : section.threads,
        }))
        .filter(section => section.threads.length);
    };

    const userNames = Array.from(
      new Set(comments.map(x => x.userName || '').filter(Boolean)),
    ).sort((x, y) => y.length - x.length);

    const renderText = (text: string) => {
      const mention = userNames.find(name => text.startsWith(`@${name}`));
      if (!mention) {
        return text;
      }
      return (
        <>
          <span className="CommentMention">@{mention}</span>
          {text.slice(mention.length + 1)}
        </>
      );
    };

    const renderComment = (comment: CourseComment, isReply = false) => (
      <div
        className={isReply ? 'CommentRow CommentReply' : 'CommentRow'}
        key={comment.id}>
        <div className="CommentAvatarPlaceholder">
          {getFirstCapitals(comment.userName || '')}
        </div>
        <div className="CommentContent">
          <div className="CommentHeader">
            <span className="CommentAuthor">{comment.userName}</span>
            <span className="CommentDate">
              {formatTimeAgo(comment.dateCreated)}
            </span>
          </div>
          <p className="CommentBody">{renderText(comment.text)}</p>
        </div>
      </div>
    );

    const renderSection = (section: CommentsSection) => {
      const repliesTotal = section.threads.reduce(
        (sum, thread) => sum + thread.replies.length,
        0,
      );
      return (
        <div className="CommentsSection" key={section.key}>
          <div className="CommentsSectionHeader">
            {section.isMicroreel && (
              <div className="CommentsReelThumb" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            )}
            <div className="CommentsSectionMeta">
              {section.isMicroreel && (
                <span className="CommentsSectionLabel">
                  {analyticsText.microreel}
                </span>
              )}
              <span className="CommentsSectionTitle">{section.title}</span>
            </div>
            <span className="CommentsSectionCount">
              {analyticsText.commentsCount(section.threads.length)}
              {repliesTotal > 0 &&
                ` · ${analyticsText.repliesCount(repliesTotal)}`}
            </span>
          </div>
          {section.threads.map(thread => (
            <div className="CommentThread" key={thread.root.id}>
              {renderComment(thread.root)}
              {thread.replies.length > 0 && (
                <div className="CommentReplies">
                  <span className="CommentRepliesLabel">
                    {analyticsText.repliesCount(thread.replies.length)}
                  </span>
                  {thread.replies.map(reply => renderComment(reply, true))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    };

    return (
      <div>
        <AnalyticsNavigationTabs
          onExport={onExport}
          search={search}
          setSearch={setSearch}
          tabs={courseLevelAnalyticsTabs(organizationId, courseId)}
        />
        {isLoading ? (
          <AnalyticsCommentsSkeleton />
        ) : !comments.length ? (
          <AnalyticsEmptyState
            title={analyticsText.noComments}
            showInfo={false}
          />
        ) : (
          <div className="CommentsListContainer">
            {getSections().map(renderSection)}
          </div>
        )}
      </div>
    );
  },
);

export default CourseAnalyticsCommentsUI;
